import { useCallback , useState , useEffect , useRef } from 'react';
import './../../styles/Editor.css';
import { useNavigate, useParams } from 'react-router-dom';
import EditorFieldOptions from './EditorFieldOptions.js';
import EditorVersions from './EditorVersions.js';
import FabricCanvas from './FabricCanvas.js';
import Select from './Select.js';
import SaveProductModal from './SaveProductModal.js';
import { useToast } from '../../context/ToastProvider.js';
import LoadProductTemplateModal from './LoadProductTemplateModal.js';
import TextField from './TextField.js';
import config from '../../constants/config.js';
import LayerCopyModal from './LayerCopyModal.js';
import PreviewLinksMenu from './PreviewLinksMenu.js';

function Editor({ 
    product, 
    variants,
    shops,
    toggleTheme, 
    theme, 
    toggleView, 
    view, 
    views,
    personalizationOptions,
    layerConfig,
    setLayerConfig,
    savedTemplates
}) {
    /**
     * React Required Hooks / Constant variables
    */
    const navigate = useNavigate();
    const addToast = useToast();
    const { handle } = useParams();
    const sides = [ "front","back" ];
    const sideOptions = [ { value: 'front' , id: 0 } , { value: 'back' , id: 1 } ];
    const categoryOptions = [
        {
            id: 0,
            value: "Mini Dog Tags"
        },
        {
            id: 1,
            value: "Skinny Bar Necklaces"
        },
        {
            id: 2,
            value: "Lockets"
        },
        {
            id: 3,
            value: "Circle Tags"
        },
        {
            id: 4,
            value: "Heart Tags"
        },
        {
            id: 5,
            value: "Nameplates"
        },
        {
            id: 6,
            value: "Crosses"
        }
    ];
    const debugLogging = false;

    /**
     * Layer Related state variables
    */
    const [ selectedSide, setSelectedSide ] = useState( sides[0] );
    const [ selectedVariantOption, setSelectedVariantOption ] = useState( variants[0]?.value || '' );
    const [ selectedLayerId , setSelectedLayerId ] = useState( -1 );
    const [ draggedLayerId, setDraggedLayerId ] = useState( null );
    const [ hoveredLayerId, setHoveredLayerId ] = useState( null );
    const copiedLayers = useRef( [] );

    /**
     * Accordion Toggles
     */
    const [ togglePersonalizationOptions , setTogglePersonalizationOptions ] = useState( true );
    const [ toggleLayers , setToggleLayers ] = useState( true );
    const [ toggleProductOptions , setToggleProductOptions ] = useState( true );
    const [ toggleFaceOptions , setToggleFaceOptions ] = useState( true );
    const [ expandLayer , setExpandLayer ] = useState( false );
    const [ toggleCanvasSettings , setToggleCanvasSettings ] = useState( true );

    /**
     * Save Modal Toggles
     */
    const [ toggleSaveModal , setToggleSaveModal ] = useState( false );
    const [ previewUrls , setPreviewUrls ] = useState( [] );

    /**
     * Layer Template Fields
     */
    const [ templateLoaderModalOpen , setTemplateLoaderModalOpen ] = useState( true );
    const [ newTemplateName , setNewTemplateName ] = useState( "" );
    const [ selectedTemplateCategory, setSelectedTemplateCategory ] = useState( categoryOptions[0].value );
    const [ savingTemplate , setSavingTemplate ] = useState( false );
    const [ openLayerCopyModal , setOpenLayerCopyModal ] = useState( false );

    /**
     * Tracks the amount of changes made
    */
    const [ changesMade , setChangesMade ] = useState( -1 );

    /**
     * Canvas settings ( UI ONLY )
    */
    const [ showGrid , setShowGrid ] = useState( true );

    /**
     * Helper functions that are not tied to layers
     */
    const goBack = useCallback( () => {
        navigate( -1 );
    } , [ navigate ] );

    const toggleAccordion = useCallback( ( currentFlag , toggleFunc ) => {
        toggleFunc( !currentFlag );
    } , [] );

    const toggleLayer = () => {
        setExpandLayer( prev => !prev );
    }

    /**
     * Layer Dragging Event functions
    */
    const dragLayerStart = useCallback(( e, id ) => {
        e.dataTransfer.effectAllowed = 'move';
        setDraggedLayerId( id );
    }, [] );

    const dragLayerEnter = useCallback( ( e , id ) => {
        if ( draggedLayerId != id ) {
            setHoveredLayerId( id );
        }
    }, [ draggedLayerId ] );

    const dragLayerExit = useCallback( ( e ) => {
        if ( !e.currentTarget.contains( e.relatedTarget ) ) {
            setHoveredLayerId( null );
        }
    }, [] );

    const dragLayerOver = useCallback( ( e ) => {
        e.preventDefault();
        return false;
    } , [] );

    const dragLayerDrop = useCallback( ( e, targetId ) => {
        e.preventDefault();

        if ( draggedLayerId === null || draggedLayerId === targetId ) {
            return;
        }

        // Get current layers array for selected side and variant option
        const currentLayers = layerConfig[ selectedSide ][ selectedVariantOption ];

        // Find indices for dragged and target layers
        const draggedIndex = currentLayers.findIndex( layer => layer.id === draggedLayerId );
        const targetIndex = currentLayers.findIndex( layer => layer.id === targetId );

        if ( draggedIndex === -1 || targetIndex === -1 ) {
            return;
        }

        // Clone and reorder layers
        const reordered = [ ...currentLayers ];
        const temp = reordered[ draggedIndex ];
        reordered[ draggedIndex ] = reordered[ targetIndex ];
        reordered[ targetIndex ] = temp;

        for ( let i = 0; i < reordered.length; i++ ) {
            reordered[i].layer_order = i + 1;
        }

        const newLayerConfig = structuredClone( layerConfig );
        newLayerConfig[ selectedSide ][ selectedVariantOption ] = reordered;

        // Update state
        setLayerConfig( newLayerConfig );
        setDraggedLayerId( null );
        setHoveredLayerId( null );
    }, [
        draggedLayerId,
        layerConfig,
        selectedSide,
        selectedVariantOption,
        setLayerConfig
    ]);

    /**
     * Layer Add / Update / Remove functions
    */
    const processPersonalizationAction = useCallback( ( optionIndex ) => {
        const relatedPersonalizationDetails = personalizationOptions[ optionIndex ];
        const currentLayers = structuredClone( layerConfig[selectedSide][selectedVariantOption] );

        let ids = currentLayers.map( layer => layer.id ).sort( ( a, b ) => a - b );
        let nextAvailableId = 0;
        for ( let i = 0; i < ids.length; i++ ) {
            if ( ids[i] !== nextAvailableId ) {
                break; // found the gap
            }
            nextAvailableId++;
        }

        const newLayer = {
            id: nextAvailableId,
            title: relatedPersonalizationDetails?.layer_title,
            type: relatedPersonalizationDetails?.handle,
            field_options: structuredClone( relatedPersonalizationDetails?.properties ),
            layer_order: -1,
            related_product_handle: handle,
        };
        
        // Prepend new layer
        currentLayers.unshift( newLayer );

        for ( let i = 0; i < currentLayers.length; i++ ) {
            currentLayers[i].layer_order = i + 1;
        }

        // Clone and update layerConfig
        const newLayerConfig = structuredClone( layerConfig );
        newLayerConfig[ selectedSide ][ selectedVariantOption ] = currentLayers;

        // update all layer config
        setLayerConfig( newLayerConfig );

        // point selected layer at new id
        setSelectedLayerId( nextAvailableId );
    } , [  
        personalizationOptions,
        selectedSide,
        selectedVariantOption,
        layerConfig,
        handle,
        setLayerConfig,
        setSelectedLayerId
    ]);

    const removeLayer = useCallback( ( layerId ) => {
        const finalLayers = [];
        let deselectLayer = false;
        
        const layerConfigCopy = structuredClone( layerConfig );
        const selectedLayersToEdit = layerConfigCopy[ selectedSide ][ selectedVariantOption ];
        for ( let i = 0; i < selectedLayersToEdit.length; i++ ) {
            const currentLayer = selectedLayersToEdit[i];

            if ( currentLayer.id !== layerId ) {
                finalLayers.push( currentLayer );
            } else if ( layerId === selectedLayerId ) {
                deselectLayer = true;
            }
        }

        if ( deselectLayer ) {
            setSelectedLayerId( -1 );

            // Select the first available layer (if any)
            if ( finalLayers.length > 0 ) {
                setSelectedLayerId( finalLayers[0].id );
            }
        }

        for ( let i = 0; i < finalLayers.length; i++ ) {
            finalLayers[i].layer_order = i + 1;
        }

        layerConfigCopy[ selectedSide ][ selectedVariantOption ] = finalLayers;
        setLayerConfig( layerConfigCopy );
    }, [ 
        selectedSide,
        selectedVariantOption,
        selectedLayerId,
        layerConfig,
        setLayerConfig,
        setSelectedLayerId
    ]);

    const selectLayer = useCallback( ( layerId ) => {
        setSelectedLayerId( layerId );
    }, [ setSelectedLayerId ] );

    const resetLayerFabricProps = useCallback( ( layerToUpdate ) => {
        const newLayer = structuredClone( layerToUpdate );
        recursiveReset( newLayer );

        const currentLayers = structuredClone( layerConfig[selectedSide][selectedVariantOption] );
        const updatedLayers = [];
        for ( let i = 0; i < currentLayers.length; i++ ) {
            const layer = currentLayers[i];
            if (layer.id === newLayer.id) {
                updatedLayers.push(newLayer);
            } else {
                updatedLayers.push(layer);
            }
        }

        const newLayerConfig = structuredClone( layerConfig );
        newLayerConfig[ selectedSide ][ selectedVariantOption ] = updatedLayers;

        setSelectedLayerId( newLayer.id );
        setLayerConfig( newLayerConfig );
    }, [
        layerConfig,
        selectedSide,
        selectedVariantOption,
        setLayerConfig,
        setSelectedLayerId
    ]);

    const updateSelectedLayer = useCallback( ( newValue, dictionaryKeys, layerId ) => {
        // Clone all layers
        const currentLayerConfig = structuredClone( layerConfig );
        const currentLayers = currentLayerConfig[ selectedSide ][ selectedVariantOption ];
        let latestSelectedLayer = null;
        for ( let i = 0; i < currentLayers.length; i++ ) {
            const layer = currentLayers[i];
            if ( layerId === layer.id ) {
                let objectPtr = layer.field_options;
                for ( let j = 0; j < dictionaryKeys.length; j++ ) {
                    objectPtr = objectPtr[ dictionaryKeys[j] ];
                }

                objectPtr.value = newValue;
                latestSelectedLayer = layer;
                break;
            }
        }
        
        if ( debugLogging ) {
            console.log( '[Update Layer] Side Selected: ' , selectedSide );
            console.log( '[Update Layer] Variant Option Selected: ' , selectedVariantOption );
            console.log( '[Update Layer] Layers Config ( Copy ): ' , currentLayerConfig );
            console.log( '[Update Layer] Layers From Config Copy: ' , currentLayers );
            console.log( '[Update Layer] Layer to Update: ' , latestSelectedLayer );
        }

        setLayerConfig( currentLayerConfig );
        setSelectedLayerId( latestSelectedLayer.id );
    }, [
        layerConfig,
        selectedSide,
        selectedVariantOption,
        setLayerConfig,
        setSelectedLayerId
    ]);

    const updateSelectedVariantOption = useCallback( ( variant ) => {
        const newValue = variant?.value;
        setSelectedVariantOption( newValue );
        setSelectedLayerId( -1 );
    } , [ setSelectedLayerId ] );
    
    const updateSelectedSideOption = useCallback( ( side ) => {
        const newValue = side?.value;
        setSelectedSide( newValue );
        setSelectedLayerId( -1 );
    } , [ setSelectedLayerId ] );

    const overwriteCurrentLayers  = useCallback( () => {
        addToast( `Pasted current layers from clipboard`, false , 2000 );
        const currentLayerConfig = structuredClone( layerConfig );
        currentLayerConfig[ selectedSide ][ selectedVariantOption ] = copiedLayers.current;
        setLayerConfig( currentLayerConfig );
    } , [ copiedLayers , setLayerConfig , layerConfig , selectedSide , selectedVariantOption ] ); 

    // WARNING: DO NOT USE GLOBAL SCOPE VARIABLES! ALL REFS MUST COME FROM CALLBACK
    function onLayerUpdate( layerChanged , sideRef, optionRef , currentConfigRef ) {
        const layerConfigCopy = structuredClone( currentConfigRef );
        const updatedLayers = structuredClone( layerChanged );
        layerConfigCopy[ sideRef ][ optionRef ] = updatedLayers;
        setLayerConfig( layerConfigCopy );
    }

    function recursiveReset( obj ) {
        if ( obj && typeof( obj ) === 'object' ) {
            for ( const key in obj ) {
                if ( key === 'fabric_props' && typeof obj[ key ] === 'object' ) {
                    obj[ key ].angle = 0;
                    obj[ key ].left = 0;
                    obj[ key ].top = 0;
                    // obj[ key ].scaleX = 1;
                    // obj[ key ].scaleY = 1;
                } 
                else {
                    recursiveReset( obj[ key ] );
                }
            }
        }
    };

    function returnLayerInfo( layersToModify , layerId , key ) {
        let info = null;

        for ( let i = 0; i < layersToModify.length; i++ ) {
            if ( layersToModify[i].id === layerId ) {
                info = layersToModify[i][key];
                break;
            }
        }

        return info;
    }

    function checkIfAnyLayersConfigured( layerConfig ) {
        let hasConfig = false;

        const frontEntries = Object.entries( layerConfig?.front || {} );
        const backEntries = Object.entries( layerConfig?.back || {} );

        for ( const [ productOption , layers ] of frontEntries ) {
            if ( layers.length > 0 ) {
                hasConfig = true;
                break;
            }
        }

        if ( !hasConfig ) {
            for ( const [ productOption , layers ] of backEntries ) {
                if ( layers.length > 0 ) {
                    hasConfig = true;
                    break;
                }
            }
        }

        return hasConfig;
    }

    async function saveTemplate( templateName , templateCategory , savedConfig ) {
        setSavingTemplate( true );

        const payload = {
            name: templateName,
            category: templateCategory,
            saved_config: savedConfig
        };

        try {
            const url = `${config.app.API_URL}/api/${config.app.REACT_APP_API_VERSION}/templates`;
            const requestSettings = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify( payload )
            };

            const response = await fetch( url , requestSettings );
            const data = await response.json();
            if ( data?.errors?.length > 0 || data.savedId === -1 ) {
                addToast( `Failed to add template - ${data?.errors[0]?.message ?? "Server encounterd an error while trying to save the layers."}`, true , 5000 );
            } else {
                addToast( `Added Template "${templateName}"`, false , 5000 );
            }
        } catch ( error ) {
            console.log( error );
        }

        setSavingTemplate( false );
        return;
    }
    
    /**
     * Any time a layer mutates even a little, add 1 to changes tracked
     * This allows us to show that chrome popup if you try to leave the page with any unsaved changes
     */
    useEffect( () => {
        setChangesMade( changesMade + 1 );
    } , [ layerConfig ] );

    useEffect(() => {
        const handleBeforeUnload = ( e ) => {
            if ( changesMade > 0 ) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener( 'beforeunload', handleBeforeUnload );

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [ changesMade ] );

     /**
     * Any time the versions array is mutated we will update the preview urls to the latest version saved
     */
    useEffect( () => {
        const previewUrls = [];
        if ( product.versions.values.length > 0 ) {
            const versionProductHandle = product.versions.values[0].related_product_handle;
            const versionId = product.versions.values[0].id;
            for ( let i = 0; i < shops.length; i++ ) {
                const previewUrl = `https://${shops[i].shop}/products/${versionProductHandle}?version=${encodeURIComponent(versionId)}`;
                previewUrls.push({
                    url: previewUrl,
                    related_shop: shops[i],
                    related_handle: versionProductHandle,
                    related_version_id: versionId
                });
            }
        }

        setPreviewUrls( previewUrls );
        console.log( previewUrls )
    } , [ product.versions ] );

    return(
        <div className="product-editor-wrapper">
            <div className="header-wrap">
                <header className="product-editor-header">
                    <div className="header-block">
                        <button onClick={goBack} className="go-back-button">
                            <svg className="icon" width="20" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path 
                                    d="M4.66648 19.0625C4.66648 19.3111 4.76857 19.5496 4.95028 19.7254C5.132 19.9012 5.37846 20 5.63545 20H14.7701C15.6264 19.9991 16.4474 19.6695 17.0529 19.0837C17.6584 18.4978 17.999 17.7035 18 16.875V3.125C17.999 2.29648 17.6584 1.50217 17.0529 0.916319C16.4474 0.330469 15.6264 0.000930511 14.7701 0L5.63545 0C5.37846 0 5.132 0.098772 4.95028 0.274587C4.76857 0.450403 4.66648 0.68886 4.66648 0.9375C4.66648 1.18614 4.76857 1.4246 4.95028 1.60041C5.132 1.77623 5.37846 1.875 5.63545 1.875H14.7701C15.1126 1.87536 15.441 2.00717 15.6832 2.24152C15.9255 2.47586 16.0617 2.79359 16.0621 3.125V16.875C16.0617 17.2064 15.9255 17.5242 15.6833 17.7585C15.441 17.9928 15.1126 18.1246 14.7701 18.125H5.63545C5.37846 18.125 5.132 18.2238 4.95028 18.3996C4.76857 18.5754 4.66648 18.8139 4.66648 19.0625ZM0.283802 9.33711L4.88846 4.88195C4.97806 4.7933 5.08496 4.72273 5.20295 4.67433C5.32094 4.62592 5.44768 4.60065 5.57584 4.59996C5.704 4.59927 5.83102 4.62319 5.94956 4.67033C6.0681 4.71747 6.17579 4.78689 6.26641 4.87457C6.35703 4.96226 6.42876 5.06646 6.47747 5.18115C6.52618 5.29585 6.55088 5.41875 6.55016 5.54275C6.54943 5.66674 6.52329 5.78937 6.47325 5.90352C6.4232 6.01767 6.35025 6.12109 6.25862 6.20777L3.30823 9.0625H10.9363C11.1933 9.0625 11.4397 9.16127 11.6214 9.33709C11.8031 9.5129 11.9052 9.75136 11.9052 10C11.9052 10.2486 11.8031 10.4871 11.6214 10.6629C11.4397 10.8387 11.1933 10.9375 10.9363 10.9375H3.30827L6.25878 13.7922C6.34916 13.8792 6.42091 13.9825 6.46993 14.0964C6.51895 14.2102 6.54427 14.3323 6.54445 14.4556C6.54462 14.579 6.51964 14.7011 6.47094 14.8151C6.42224 14.929 6.35078 15.0326 6.26064 15.1198C6.17051 15.207 6.06348 15.2762 5.94568 15.3233C5.82788 15.3704 5.70163 15.3946 5.57416 15.3944C5.44669 15.3942 5.32051 15.3697 5.20285 15.3223C5.08518 15.2749 4.97834 15.2055 4.88846 15.118L0.283802 10.6629C0.102087 10.4871 0 10.2487 0 10C0 9.75138 0.102087 9.51293 0.283802 9.33711Z" 
                                    fill="black"
                                />
                            </svg>
                            <span className="sr-only">Back</span>
                        </button>
                        <h1 className="page-title">
                            { product?.primary_title } 
                            { product?.versions?.values?.length > 0 ? " / " + product?.versions?.values[0]?.name : "" }
                        </h1>
                    </div>
                    <div className="header-block">
                        { views.length > 0 && 
                            <ul className="header-tabs">
                                { views.map( ( viewInfo , index ) => {
                                    const viewId = viewInfo?.id;
                                    const viewHandle = viewInfo?.handle;
                                    const viewTitle = viewInfo?.title;
                                    const selectedView = view === viewHandle;

                                    return(
                                        <li key={viewId} className="header-tab-option">
                                            <button 
                                                onClick={() => { 
                                                    toggleView( viewHandle );
                                                }} 
                                                className={ `header-tab-option-button ${ selectedView ? "selected" : "" }` }>
                                                    {viewTitle}
                                            </button>
                                        </li>
                                    )
                                })}
                            </ul>
                        }
                    </div>
                    <div className="header-block">
                        <div className="header-actions-group">
                            <div className="switch-container">
                                <label htmlFor="toggle-ui" className="switch">
                                    <input onChange={ () => { toggleTheme() } } id="toggle-ui" type="checkbox" />
                                    <div className="icon-toggles">
                                        <svg className="icon dark" width="12" height="12" viewBox="0 0 8 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M6.85846 3.98371C6.78664 3.96576 6.71483 3.98371 6.65199 4.0286C6.41859 4.22609 6.14927 4.38768 5.85303 4.4954C5.57474 4.60313 5.26952 4.65699 4.94635 4.65699C4.21921 4.65699 3.55491 4.36075 3.07913 3.88496C2.60334 3.40918 2.3071 2.74488 2.3071 2.01774C2.3071 1.71252 2.36096 1.41628 2.45073 1.14696C2.54948 0.859699 2.69311 0.599365 2.88163 0.374939C2.96242 0.276191 2.94447 0.132559 2.84572 0.0517653C2.78288 0.00688007 2.71107 -0.011074 2.63925 0.00688007C1.8762 0.213352 1.2119 0.671181 0.736118 1.28162C0.278288 1.88308 0 2.62818 0 3.44509C0 4.42358 0.39499 5.31231 1.04134 5.95866C1.68768 6.60501 2.57641 7 3.55491 7C4.3808 7 5.14385 6.71273 5.75428 6.23695C6.3737 5.75219 6.82255 5.06096 7.01107 4.27098C7.05596 4.13632 6.98414 4.01064 6.85846 3.98371ZM5.48497 5.85991C4.9643 6.27286 4.3 6.52421 3.57286 6.52421C2.72004 6.52421 1.94802 6.17411 1.39144 5.61753C0.834865 5.06096 0.48476 4.28893 0.48476 3.43611C0.48476 2.72692 0.718163 2.08058 1.12213 1.55991C1.40042 1.20083 1.75052 0.904584 2.15449 0.698112C2.10961 0.79686 2.06472 0.895607 2.02881 1.00333C1.91211 1.32651 1.85825 1.66763 1.85825 2.02671C1.85825 2.87953 2.20835 3.66054 2.76493 4.21711C3.32151 4.77369 4.10251 5.12379 4.95533 5.12379C5.33236 5.12379 5.69145 5.06096 6.0236 4.93528C6.1403 4.89039 6.257 4.84551 6.36472 4.79164C6.14927 5.20459 5.85303 5.57265 5.48497 5.85991Z" fill="black"/>
                                        </svg>
                                        
                                        <svg className="icon light" width="12" height="12" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5.41808 2.58203C5.05829 2.22225 4.55035 1.98944 4.00009 1.98944C3.44982 1.98944 2.94188 2.21166 2.5821 2.58203C2.22231 2.94182 1.9895 3.44976 1.9895 4.00003C1.9895 4.55029 2.22231 5.05823 2.5821 5.41802C2.94188 5.77781 3.44982 6.01061 4.00009 6.01061C4.55035 6.01061 5.05829 5.78839 5.41808 5.41802C5.77787 5.05823 6.01067 4.55029 6.01067 4.00003C6.01067 3.44976 5.78845 2.94182 5.41808 2.58203ZM5.03713 5.03706C4.77257 5.30161 4.4022 5.46035 4.00009 5.46035C3.59797 5.46035 3.2276 5.30161 2.96305 5.03706C2.6985 4.77251 2.53977 4.40214 2.53977 4.00003C2.53977 3.59791 2.6985 3.22754 2.96305 2.96299C3.2276 2.69844 3.59797 2.53971 4.00009 2.53971C4.4022 2.53971 4.77257 2.69844 5.03713 2.96299C5.30168 3.22754 5.46041 3.59791 5.46041 4.00003C5.46041 4.40214 5.30168 4.77251 5.03713 5.03706Z" fill="black"/>
                                            <path d="M7.72496 3.72485H6.91014C6.76199 3.72485 6.63501 3.85184 6.63501 3.99999C6.63501 4.14813 6.76199 4.27512 6.91014 4.27512H7.72496C7.87311 4.27512 8.00009 4.14813 8.00009 3.99999C8.00009 3.85184 7.87311 3.72485 7.72496 3.72485Z" fill="black"/>
                                            <path d="M3.99999 6.63495C3.85184 6.63495 3.72485 6.76193 3.72485 6.91008V7.7249C3.72485 7.87305 3.85184 8.00003 3.99999 8.00003C4.14813 8.00003 4.27512 7.87305 4.27512 7.7249V6.91008C4.27512 6.76193 4.14813 6.63495 3.99999 6.63495Z" fill="black"/>
                                            <path d="M6.82529 6.44446L6.24328 5.86245C6.14804 5.75663 5.96814 5.75663 5.86232 5.86245C5.7565 5.96827 5.7565 6.13758 5.86232 6.2434L6.44434 6.82541C6.55016 6.93123 6.71947 6.93123 6.82529 6.82541C6.93111 6.71959 6.93111 6.55028 6.82529 6.44446Z" fill="black"/>
                                            <path d="M3.99999 0C3.85184 0 3.72485 0.126984 3.72485 0.275133V1.08995C3.72485 1.2381 3.85184 1.36508 3.99999 1.36508C4.14813 1.36508 4.27512 1.2381 4.27512 1.08995V0.275133C4.27512 0.126984 4.14813 0 3.99999 0Z" fill="black"/>
                                            <path d="M6.83579 1.17464C6.72997 1.06882 6.56065 1.06882 6.45483 1.17464L5.87282 1.75665C5.767 1.86247 5.767 2.03179 5.87282 2.13761C5.96806 2.24343 6.14795 2.24343 6.25377 2.13761L6.83579 1.55559C6.94161 1.44977 6.94161 1.28046 6.83579 1.17464Z" fill="black"/>
                                            <path d="M1.08995 3.72485H0.275133C0.126984 3.72485 0 3.85184 0 3.99999C0 4.14813 0.116402 4.27512 0.275133 4.27512H1.08995C1.2381 4.27512 1.36508 4.14813 1.36508 3.99999C1.36508 3.85184 1.2381 3.72485 1.08995 3.72485Z" fill="black"/>
                                            <path d="M2.1268 5.86245C2.03156 5.75663 1.85167 5.75663 1.74585 5.86245L1.16384 6.44446C1.05802 6.55028 1.05802 6.71959 1.16384 6.82541C1.26966 6.93123 1.43897 6.93123 1.54479 6.82541L2.1268 6.2434C2.23262 6.13758 2.23262 5.96827 2.1268 5.86245Z" fill="black"/>
                                            <path d="M2.1268 1.75665L1.54479 1.17464C1.43897 1.06882 1.26966 1.06882 1.16384 1.17464C1.05802 1.28046 1.05802 1.44977 1.16384 1.55559L1.74585 2.13761C1.85167 2.24343 2.02098 2.24343 2.1268 2.13761C2.23262 2.03179 2.23262 1.86247 2.1268 1.75665Z" fill="black"/>
                                        </svg>
                                    </div>
                                </label>
                            </div>

                            <button onClick={ () => { setToggleSaveModal( true ); } } className="button secondary-action">Save Updates</button>
                            <PreviewLinksMenu previewUrls={previewUrls} />
                        </div>
                    </div>
                </header>
            </div>
            
            { view === 'edit' &&
                <section className="product-editor-body">
                    <div className="layers">
                        <div className="accordion product-options-select">
                            <button onClick={ () => toggleAccordion( toggleProductOptions , setToggleProductOptions ) } className="accordion-btn">
                                { !toggleProductOptions && 
                                    <span className="collapsed">
                                        <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M10 0.5L6.70898 0.5L0 0.500001L5 6.5L10 0.5Z" fill="#A4A4A4"/>
                                        </svg>
                                    </span>
                                }

                                { toggleProductOptions && 
                                    <span className="expanded">
                                        <svg 
                                            width="10" 
                                            height="7" 
                                            viewBox="0 0 10 7" 
                                            fill="none" 
                                            xmlns="http://www.w3.org/2000/svg" 
                                            style={{ transform: 'rotate(180deg)', transformOrigin: 'center' }}
                                        >
                                            <path d="M10 0.5L6.70898 0.5L0 0.500001L5 6.5L10 0.5Z" fill="#A4A4A4"/>
                                        </svg>
                                    </span>
                                }
                                
                                <span>Product Options</span>
                            </button>

                            { toggleProductOptions &&
                                <div className="accordion-content">
                                    { variants.length > 0 && (
                                        <Select  
                                            label={""} 
                                            defaultValue={selectedVariantOption} 
                                            gridWidth={"FULL"} 
                                            icon={ null } 
                                            options={ variants }
                                            onChange={ updateSelectedVariantOption }
                                        /> 
                                    )}
                                </div>
                            }
                        </div>

                        <div className="accordion product-face-select">
                            <button onClick={ () => toggleAccordion( toggleFaceOptions , setToggleFaceOptions ) } className="accordion-btn">
                                { !toggleFaceOptions && 
                                    <span className="collapsed">
                                        <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M10 0.5L6.70898 0.5L0 0.500001L5 6.5L10 0.5Z" fill="#A4A4A4"/>
                                        </svg>
                                    </span>
                                }

                                { toggleFaceOptions && 
                                    <span className="expanded">
                                        <svg 
                                            width="10" 
                                            height="7" 
                                            viewBox="0 0 10 7" 
                                            fill="none" 
                                            xmlns="http://www.w3.org/2000/svg" 
                                            style={{ transform: 'rotate(180deg)', transformOrigin: 'center' }}
                                        >
                                            <path d="M10 0.5L6.70898 0.5L0 0.500001L5 6.5L10 0.5Z" fill="#A4A4A4"/>
                                        </svg>
                                    </span>
                                }
                                
                                <span>Product Sides</span>
                            </button>

                            { toggleFaceOptions &&
                                <div className="accordion-content">
                                    { sides.length > 0 && (
                                        <Select  
                                            label={""} 
                                            defaultValue={selectedSide} 
                                            gridWidth={"FULL"} 
                                            icon={ null } 
                                            options={ sideOptions }
                                            onChange={ updateSelectedSideOption }
                                        /> 
                                    )}
                                </div>
                            }
                        </div>

                        <div className="accordion personalization-actions-dropdown">
                            <button onClick={ () => toggleAccordion( togglePersonalizationOptions , setTogglePersonalizationOptions ) } className="accordion-btn">
                                { !togglePersonalizationOptions && 
                                    <span className="collapsed">
                                        <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M10 0.5L6.70898 0.5L0 0.500001L5 6.5L10 0.5Z" fill="#A4A4A4"/>
                                        </svg>
                                    </span>
                                }

                                { togglePersonalizationOptions && 
                                    <span className="expanded">
                                        <svg 
                                            width="10" 
                                            height="7" 
                                            viewBox="0 0 10 7" 
                                            fill="none" 
                                            xmlns="http://www.w3.org/2000/svg" 
                                            style={{ transform: 'rotate(180deg)', transformOrigin: 'center' }}
                                        >
                                            <path d="M10 0.5L6.70898 0.5L0 0.500001L5 6.5L10 0.5Z" fill="#A4A4A4"/>
                                        </svg>
                                    </span>
                                }
                                
                                <span>Personalizations Layers</span>
                            </button>

                            { togglePersonalizationOptions &&
                                <div className="accordion-content">
                                    <div className="personalization-actions">
                                        { personalizationOptions.map( ( option , index ) => {
                                            return(
                                                <button key={index} onClick={ () => { processPersonalizationAction( index ) }} className="personalization-action">
                                                    <div dangerouslySetInnerHTML={ { __html: option.icon } }></div>
                                                    <div>
                                                        { option.title }
                                                    </div>
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            }
                        </div>

                        <div className="accordion canvas-settings-dropdown">
                            <button
                                onClick={ () => toggleAccordion( toggleCanvasSettings , setToggleCanvasSettings ) }
                                className="accordion-btn"
                            >
                                { !toggleCanvasSettings &&
                                    <span className="collapsed">
                                        <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M10 0.5L6.70898 0.5L0 0.500001L5 6.5L10 0.5Z" fill="#A4A4A4"/>
                                        </svg>
                                    </span>
                                }

                                { toggleCanvasSettings &&
                                    <span className="expanded">
                                        <svg
                                            width="10"
                                            height="7"
                                            viewBox="0 0 10 7"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            style={{ transform: 'rotate(180deg)', transformOrigin: 'center' }}
                                        >
                                            <path d="M10 0.5L6.70898 0.5L0 0.500001L5 6.5L10 0.5Z" fill="#A4A4A4"/>
                                        </svg>
                                    </span>
                                }

                                <span>Canvas Settings</span>
                            </button>

                            { toggleCanvasSettings &&
                                <div className="accordion-content">
                                    <label className="canvas-setting-toggle">
                                        <span className="label-text">Show Grid</span>
                                        <input
                                            type="checkbox"
                                            checked={showGrid}
                                            onChange={(e) => {
                                                setShowGrid( e.target.checked );
                                            }}
                                        />
                                        <span className="slider"></span>
                                        
                                    </label>
                                </div>
                            }
                        </div>

                        <div className="accordion layers-dropdown">
                            <div className="action-row">
                                <button onClick={ () => toggleAccordion( toggleLayers , setToggleLayers ) } className="accordion-btn">
                                    <span>Layers</span>

                                    { !toggleLayers && 
                                        <span className="expanded">
                                            <svg width="11" height="7" viewBox="0 0 11 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M5.39998 6.5C5.20642 6.5 5.01289 6.42797 4.86532 6.28422L0.221553 1.75832C-0.073851 1.47041 -0.073851 1.00363 0.221553 0.71584C0.516838 0.428053 0.995689 0.428053 1.29112 0.71584L5.39998 4.72061L9.50887 0.71598C9.80427 0.428193 10.2831 0.428193 10.5783 0.71598C10.8739 1.00377 10.8739 1.47055 10.5783 1.75846L5.93464 6.28436C5.787 6.42814 5.59347 6.5 5.39998 6.5Z" fill="white"/>
                                            </svg>
                                        </span>
                                    }

                                    { toggleLayers && 
                                        <span className="collapsed">
                                            <svg 
                                                width="11" 
                                                height="7" 
                                                viewBox="0 0 11 7" 
                                                fill="none" 
                                                xmlns="http://www.w3.org/2000/svg"
                                                style={{ transform: 'rotate(180deg)', transformOrigin: 'center' }}
                                            >
                                                <path d="M5.39998 6.5C5.20642 6.5 5.01289 6.42797 4.86532 6.28422L0.221553 1.75832C-0.073851 1.47041 -0.073851 1.00363 0.221553 0.71584C0.516838 0.428053 0.995689 0.428053 1.29112 0.71584L5.39998 4.72061L9.50887 0.71598C9.80427 0.428193 10.2831 0.428193 10.5783 0.71598C10.8739 1.00377 10.8739 1.47055 10.5783 1.75846L5.93464 6.28436C5.787 6.42814 5.59347 6.5 5.39998 6.5Z" fill="white"/>
                                            </svg>
                                        </span>
                                    }
                                </button>

                                <div className="layer-actions">
                                    <button onClick={() => setOpenLayerCopyModal(true)} className="layer-action-button" title="Copy Layers">
                                        <svg width="15" height="15" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                            <path fill="white" d="M384 352l-160 0c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l108.1 0c4.2 0 8.3 1.7 11.3 4.7l67.9 67.9c3 3 4.7 7.1 4.7 11.3L416 320c0 17.7-14.3 32-32 32zM433.9 81.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1L224 0c-35.3 0-64 28.7-64 64l0 256c0 35.3 28.7 64 64 64l160 0c35.3 0 64-28.7 64-64l0-204.1c0-12.7-5.1-24.9-14.1-33.9zM64 128c-35.3 0-64 28.7-64 64L0 448c0 35.3 28.7 64 64 64l160 0c35.3 0 64-28.7 64-64l0-32-32 0 0 32c0 17.7-14.3 32-32 32L64 480c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l64 0 0-32-64 0z"/>
                                        </svg>
                                        <span className="sr-only">Copy Layers</span>
                                    </button>
                                    { copiedLayers?.current?.length > 0 && (
                                        <button onClick={ overwriteCurrentLayers } className="layer-action-button" title="Replace Layers">
                                            <svg width="15" height="15" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                                <path fill="white" d="M136.5 51.2C138.7 40.2 148.4 32 160 32s21.3 8.2 23.5 19.2C185 58.6 191.6 64 199.2 64l8.8 0c8.8 0 16 7.2 16 16l0 16L96 96l0-16c0-8.8 7.2-16 16-16l8.8 0c7.6 0 14.2-5.4 15.7-12.8zM160 0c-22.3 0-41.6 13.1-50.6 32L64 32C28.7 32 0 60.7 0 96L0 384c0 35.3 28.7 64 64 64l128 0 0-32L64 416c-17.7 0-32-14.3-32-32L32 96c0-17.7 14.3-32 32-32l2.7 0C65 69 64 74.4 64 80l0 16c0 17.7 14.3 32 32 32l112 0c11.5-15.4 28.5-26.4 48-30.4c0-.5 0-1.1 0-1.6l0-16c0-5.6-1-11-2.7-16l2.7 0c17.7 0 32 14.3 32 32l32 0c0-35.3-28.7-64-64-64l-45.4 0c-9-18.9-28.3-32-50.6-32zM448 480l-160 0c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l108.1 0c4.2 0 8.3 1.7 11.3 4.7l67.9 67.9c3 3 4.7 7.1 4.7 11.3L480 448c0 17.7-14.3 32-32 32zm49.9-270.1l-67.9-67.9c-9-9-21.2-14.1-33.9-14.1L288 128c-35.3 0-64 28.7-64 64l0 256c0 35.3 28.7 64 64 64l160 0c35.3 0 64-28.7 64-64l0-204.1c0-12.7-5.1-24.9-14.1-33.9z"/>
                                            </svg>
                                            <span className="sr-only">Replace Layers</span>
                                        </button>
                                    )}
                                    
                                </div>
                            </div>

                            { toggleLayers &&
                                <div className={`accordion-content ${ draggedLayerId !== null ? 'dragging' : ''}`}>
                                    { layerConfig[ selectedSide ][ selectedVariantOption]?.length <= 0 &&
                                        <p className="no-layers">
                                            No layers found. Select a personalization option to get started. 
                                        </p>
                                    }

                                    { layerConfig[ selectedSide ][ selectedVariantOption]?.length > 0 &&
                                        layerConfig[ selectedSide ][ selectedVariantOption].map( ( layer , index ) => {
                                            const isSelected = selectedLayerId === layer?.id;
                                            const isHovered = hoveredLayerId === layer.id;
                                            const isGettingDragged = draggedLayerId === layer.id;

                                            return(
                                                <div 
                                                    key={layer.id}
                                                    draggable={true}
                                                    onDragStart={ (e) => dragLayerStart( e, layer.id ) }
                                                    onDragEnd={ () => setDraggedLayerId( null ) }
                                                    onDragEnter={ ( e ) => dragLayerEnter( e, layer.id ) }
                                                    onDragLeave={ dragLayerExit }
                                                    onDragOver={ dragLayerOver }
                                                    onDrop={ (e) => dragLayerDrop( e, layer.id ) }
                                                    className={`layer-option ${isSelected ? 'selected' : ''} ${isHovered ? 'selected-swap' : ''} ${isGettingDragged ? 'dragging' : ''}`} 
                                                >
                                                    <button className="layer-select" onClick={ () => { selectLayer( layer.id ) }}>
                                                        <span>{ layer?.title }</span>
                                                    </button>
                                                    <button title="reset layer transform" className="layer-remove" onClick={ () => {  
                                                        resetLayerFabricProps( layer );
                                                    }}>
                                                        <span className="sr-only">reset layer transform</span>
                                                        <svg width="14" height="22" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                                            <path d="M40 224c-13.3 0-24-10.7-24-24L16 56c0-13.3 10.7-24 24-24s24 10.7 24 24l0 80.1 20-23.5C125 63.4 186.9 32 256 32c123.7 0 224 100.3 224 224s-100.3 224-224 224c-50.4 0-97-16.7-134.4-44.8c-10.6-8-12.7-23-4.8-33.6s23-12.7 33.6-4.8C179.8 418.9 216.3 432 256 432c97.2 0 176-78.8 176-176s-78.8-176-176-176c-54.3 0-102.9 24.6-135.2 63.4l-.1 .2s0 0 0 0L93.1 176l90.9 0c13.3 0 24 10.7 24 24s-10.7 24-24 24L40 224z"/>
                                                        </svg>
                                                    </button>
                                                    <button title="remove layer" className="layer-remove" onClick={ () => { removeLayer( layer.id ) }}>
                                                        <span className="sr-only">remove</span>
                                                        <svg width="14" height="22" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                                            <path d="M164.2 39.5L148.9 64l150.3 0L283.8 39.5c-2.9-4.7-8.1-7.5-13.6-7.5l-92.5 0c-5.5 0-10.6 2.8-13.6 7.5zM311 22.6L336.9 64 384 64l32 0 16 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-16 0 0 336c0 44.2-35.8 80-80 80l-224 0c-44.2 0-80-35.8-80-80L32 96 16 96C7.2 96 0 88.8 0 80s7.2-16 16-16l16 0 32 0 47.1 0L137 22.6C145.8 8.5 161.2 0 177.7 0l92.5 0c16.6 0 31.9 8.5 40.7 22.6zM64 96l0 336c0 26.5 21.5 48 48 48l224 0c26.5 0 48-21.5 48-48l0-336L64 96zm80 80l0 224c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-224c0-8.8 7.2-16 16-16s16 7.2 16 16zm96 0l0 224c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-224c0-8.8 7.2-16 16-16s16 7.2 16 16zm96 0l0 224c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-224c0-8.8 7.2-16 16-16s16 7.2 16 16z"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            }
                        </div>
                    </div>
                    
                    <div className="editor-view">
                        <div className="canvas-wrapper">
                            <FabricCanvas
                                layers={layerConfig[ selectedSide ][ selectedVariantOption ]}
                                selectedSide={ selectedSide }
                                selectedVariantOption={ selectedVariantOption }
                                layerConfig={layerConfig}
                                selectedLayerId={selectedLayerId}
                                setSelectedLayerId={setSelectedLayerId}
                                onLayerUpdate={ onLayerUpdate }
                                showGrid={showGrid}
                            />
                        </div>
                    </div>

                    <div className="layer-editor">
                        <div className="layer-editor-section">
                            <h2 className="product-title">{ product?.primary_title }</h2>
                        </div>

                        { selectedLayerId !== -1 &&
                            <EditorFieldOptions 
                                key={ selectedLayerId }
                                fieldOptions={ returnLayerInfo( layerConfig[ selectedSide ][ selectedVariantOption ] , selectedLayerId , 'field_options' ) }
                                layerName={ returnLayerInfo( layerConfig[ selectedSide ][ selectedVariantOption ] , selectedLayerId , 'title' ) }
                                debug={ false }
                                expandedOnLoad={ true }
                                updateLayer={ updateSelectedLayer }
                                layerId={ selectedLayerId }
                            />                            
                        }

                        <div className="layer-editor-section export">
                            <div className="export-accordion">
                                <button className="toggle-btn" onClick={ toggleLayer }>
                                    <span>
                                        Save Template
                                    </span>

                                    { expandLayer &&
                                        <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M11.3999 0.5H6.60002H5.99995C5.66882 0.5 6.60002 0.5 5.39998 0.5H0.599962C0.268835 0.5 0 0.768834 0 1.09996C0 1.4312 0.268835 1.70003 0.599962 1.70003H5.39998C5.39998 1.70003 7.08418 1.70003 5.73985 1.69999C4.39552 1.69995 5.66882 1.69999 5.99995 1.69999C6.33118 1.69999 5.17012 1.70003 6.45186 1.69999L7.23032 1.70003H6.60002H11.3999C11.7312 1.70003 12 1.4312 12 1.09996C12 0.768834 11.7312 0.5 11.3999 0.5Z" fill="white"/>
                                        </svg>
                                    }

                                    { !expandLayer &&
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M11.3999 5.39998H6.60002V0.599962C6.60002 0.268835 6.33118 0 5.99995 0C5.66882 0 5.39998 0.268835 5.39998 0.599962V5.39998H0.599962C0.268835 5.39998 0 5.66882 0 5.99995C0 6.33118 0.268835 6.60002 0.599962 6.60002H5.39998V11.3999C5.39998 11.7312 5.66882 12 5.99995 12C6.33118 12 6.60002 11.7312 6.60002 11.3999V6.60002H11.3999C11.7312 6.60002 12 6.33118 12 5.99995C12 5.66882 11.7312 5.39998 11.3999 5.39998Z" fill="white"/>
                                        </svg>
                                    }
                                </button>
                                <div className={ "export-accordion-content" + ( expandLayer ? ' visible' : '' ) }>
                                    {/* Field for a template name - blank by default */}
                                    <TextField 
                                        label={"Template Name"} 
                                        defaultValue={newTemplateName} 
                                        gridWidth={"FULL"} 
                                        icon={null}
                                        onChange={ ( newValue ) => {
                                           setNewTemplateName( newValue );
                                        }}
                                        visible={true}
                                    /> 

                                    <div style={{ marginTop: "10px" }}></div>

                                    <Select  
                                        label={"Template Category"} 
                                        defaultValue={selectedTemplateCategory} 
                                        gridWidth={"FULL"} 
                                        icon={ null } 
                                        options={categoryOptions}
                                        onChange={ ( newValue ) => {
                                            setSelectedTemplateCategory( newValue.value );
                                        }}
                                    /> 

                                    <button 
                                        onClick={ () => {
                                            saveTemplate( newTemplateName , selectedTemplateCategory, layerConfig );
                                        }}
                                        disabled={savingTemplate}
                                        className="export-template-button">
                                        { savingTemplate ? 
                                            <svg width="15" height="15" viewBox="0 0 50 50" className="spinner">
                                                <circle cx="25" cy="25" r="20" stroke="white" strokeWidth="5" fill="none" strokeDasharray="90 150" strokeLinecap="round">
                                                    <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                                                </circle>
                                            </svg>
                                        : "Save" }
                                    </button>
                                    
                                    {/* Export Button */}
                                        {/* sends name, category, saved_config ( saved_config = layerConfig ) */}

                                </div>
                            </div>
                        </div>
                    </div>

                    { toggleSaveModal && (
                        <SaveProductModal
                            onClose={ () => {
                                setToggleSaveModal( false )
                            }}
                            onSaveFinish={ ( data ) => {
                                setToggleSaveModal( false );
                                setChangesMade( 0 );

                                try {
                                    product.versions = null;
                                    product.versions = data.versions;
                                } catch ( error ) {
                                    console.log( error );
                                }
                            }}
                            layerConfig={layerConfig}
                            productHandle={handle}
                            shops={shops}
                        ></SaveProductModal>
                    )}

                    { openLayerCopyModal && (
                        <LayerCopyModal 
                            currentConfig={ layerConfig }
                            currentSide={ selectedSide }
                            currentOption={ selectedVariantOption }
                            templates={savedTemplates}
                            productHandle={handle}
                            onClose={ () => {
                                setOpenLayerCopyModal( false );
                            }}
                            onCopiedCurrentLayer={ () => {
                                addToast( `Copied current layers to clipboard`, false , 2000 );
                                copiedLayers.current = null;
                                copiedLayers.current = layerConfig[ selectedSide ][selectedVariantOption];
                                setOpenLayerCopyModal( false );
                            }}
                            onCopiedTemplateLayer={ ( selectedSideAndOptionLayers ) => {
                                addToast( `Copied template layers to clipboard`, false , 2000 );
                                copiedLayers.current = null;
                                copiedLayers.current = selectedSideAndOptionLayers;
                                console.log( selectedSideAndOptionLayers );
                                setOpenLayerCopyModal( false );
                            }}
                        />
                    )}
                </section>
            }
            
            { view === 'versions' &&
                <EditorVersions handle={handle} versions={product?.versions} setChangesMade={setChangesMade} />
            }

            { view === 'history' &&
                <section className="history-body">
                    
                </section>
            }
            
            { view === 'orders' &&
                <section className="orders-body">
                    
                </section>
            }

            { view === 'ab-tests' &&
                <section className="ab-tests-body">
                    
                </section>
            }

            { templateLoaderModalOpen === true && checkIfAnyLayersConfigured( layerConfig ) === false &&
                <LoadProductTemplateModal 
                    savedTemplates={ savedTemplates }
                    onClose={() => {
                        setTemplateLoaderModalOpen( false );
                    }}
                    onLoadedTemplate={ ( updatedLayers ) => {
                        // take layer config replied with and set to our current layers
                        setLayerConfig( updatedLayers );
                        setTemplateLoaderModalOpen( false );
                    }}
                    currentConfig={ layerConfig }
                    productHandle={handle}
                />
            }
        </div>
    );
}

export default Editor;